/**
 * Public password recovery request — always shows a generic success message.
 */
import {
	Alert,
	Box,
	Button,
	Link,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useState, type FormEvent } from "react";
import { Link as RouterLink } from "react-router-dom";

import { getApiClient } from "../services/apiClientProvider";

export function ForgotPasswordPage() {
	const [email, setEmail] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [done, setDone] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	async function onSubmit(e: FormEvent) {
		e.preventDefault();
		setError(null);
		if (!email.trim()) {
			setError("Email is required.");
			return;
		}
		setSubmitting(true);
		try {
			await getApiClient().forgotPassword({ email: email.trim() });
			setDone(true);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Request failed.");
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Box data-testid="forgot-password-surface">
			<Stack spacing={1} sx={{ mb: 3 }}>
				<Typography
					variant="overline"
					sx={{ color: "primary.main", letterSpacing: "0.12em", fontWeight: 700 }}
				>
					Account recovery
				</Typography>
				<Typography component="h1" variant="h4">
					Reset your password
				</Typography>
				<Typography variant="body2" sx={{ color: "text.secondary" }}>
					Enter the email for your FlahaSOIL account. If it exists, we will send a
					reset link via Flaha Mail.
				</Typography>
			</Stack>

			{done ? (
				<Stack spacing={2}>
					<Alert severity="success" role="status">
						If an account exists for that email, a reset link is on its way.
						Check your inbox and spam folder.
					</Alert>
					<Button component={RouterLink} to="/login" variant="contained">
						Back to sign in
					</Button>
				</Stack>
			) : (
				<form noValidate onSubmit={onSubmit}>
					<Stack spacing={2.25}>
						{error && (
							<Alert severity="error" role="alert">
								{error}
							</Alert>
						)}
						<TextField
							label="Email"
							type="email"
							autoComplete="email"
							required
							fullWidth
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						/>
						<Button
							type="submit"
							variant="contained"
							size="large"
							fullWidth
							disabled={submitting}
						>
							{submitting ? "Sending…" : "Send reset link"}
						</Button>
						<Typography
							variant="body2"
							sx={{ textAlign: "center", color: "text.secondary" }}
						>
							<Link component={RouterLink} to="/login" fontWeight={700}>
								Sign in
							</Link>
						</Typography>
					</Stack>
				</form>
			)}
		</Box>
	);
}
